/**
 * 品牌名（10-06）：两个主体共用同一个前端包，品牌名不能写死——一律读系统设置「通用设置」里的品牌名。
 * 走无需登录的 /api/public/branding：浏览器标签页标题、登录 / 找回密码 / 重置密码 / MFA 页在登录前就要显示。
 * 没取到之前只显示中性的「悠途」，不显示任何一个主体的英文名（免得公司站先闪一下 YouTu）。
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

export interface PublicBranding {
  brand_name: string;
  legal_name: string;
  logo_url: string | null;
}

const NEUTRAL = "悠途";

async function fetchPublicBranding(): Promise<PublicBranding> {
  const r = await fetch("/api/public/branding", { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`branding ${r.status}`);
  return r.json();
}

export function usePublicBranding(): PublicBranding | undefined {
  const { data } = useQuery({ queryKey: ["public-branding"], queryFn: fetchPublicBranding, staleTime: 10 * 60_000, retry: 1 });
  return data;
}

/** BRANDUI-1 · 对外单据 / 页面抬头用的品牌短名：品牌名 → 公司名 → 空串（空 = 调用方不显示这一项，不拿任何主体的名字兜底）。 */
export function brandShort(b: Pick<PublicBranding, "brand_name" | "legal_name"> | null | undefined): string {
  return (b?.brand_name ?? "").trim() || (b?.legal_name ?? "").trim();
}

export function useBrandShort(): string {
  return brandShort(usePublicBranding());
}

/** 品牌名文字（取不到时显示中性的「悠途」）。 */
export function useBrandName(): string {
  return usePublicBranding()?.brand_name?.trim() || NEUTRAL;
}

/** 挂在应用根部：把浏览器标签页标题设成「{品牌名} · 运营后台」。 */
export function BrandDocumentTitle() {
  const name = useBrandName();
  useEffect(() => {
    document.title = `${name} · 运营后台`;
  }, [name]);
  return null;
}

/** 品牌名文字组件（替换各处写死的「悠途 YouTu」）。 */
export function BrandName() {
  return <>{useBrandName()}</>;
}
